import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useTranslation } from '../hooks/useTranslation';

interface NewsArticle {
  title: string;
  summary: string;
  link: string;
  imageUrl: string | null;
  publishedAt: string;
  source: string;
}

const SOURCE_COLORS: Record<string, string> = {
  'VRT NWS': '#E3170D',
  'Het Laatste Nieuws': '#FF6B00',
  'De Standaard': '#1A1A1A',
  'De Morgen': '#0066CC',
};

export function NewsWidget() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, isLoading } = useApi<{ articles: NewsArticle[] }>('/api/v1/user/news?feeds=vrt,hln');
  const [currentIdx, setCurrentIdx] = useState(0);
  const [isTall, setIsTall] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const containerRef = useRef<HTMLDivElement>(null);

  const articles = data?.articles?.slice(0, 5) || [];

  // Detect tall mode via ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setIsTall(entry.contentRect.height > 100);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Auto-rotate headlines every 8 seconds (only in ticker mode)
  useEffect(() => {
    if (articles.length <= 1 || isTall) return;
    timerRef.current = setInterval(() => {
      setCurrentIdx((prev) => (prev + 1) % articles.length);
    }, 8000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [articles.length, isTall]);

  // T6.7 / D8: `backdropFilter: 'blur(8px)'` removed — `--caro-card-bg`
  // is opaque in every theme so the blur had no visible effect.
  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--caro-card-bg)',
    borderRadius: '16px',
    padding: '14px 20px',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid var(--caro-border)',
    height: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  };

  // Loading or no articles — show placeholder linking to /news
  if (articles.length === 0) {
    return (
      <div ref={containerRef} onClick={() => navigate('/news')} style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: 'var(--font-body)' }}>{'\u{1F4F0}'}</span>
          <div style={{ fontSize: 'var(--font-body)', fontWeight: 500, color: 'var(--caro-text)' }}>
            {t.news.title}
          </div>
          <div style={{ fontSize: 'var(--font-body)', color: 'var(--caro-text-light)', marginLeft: 'auto' }}>
            {isLoading ? t.common.loading : '\u25B6'}
          </div>
        </div>
      </div>
    );
  }

  // ── Tall mode: vertical headline list ──────────────────────
  if (isTall) {
    return (
      <div ref={containerRef} onClick={() => navigate('/news')} style={cardStyle}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '10px',
        }}>
          <span style={{ fontSize: 'var(--font-body)' }}>{'\u{1F4F0}'}</span>
          <span style={{ fontSize: 'var(--font-body)', fontWeight: 700, color: 'var(--caro-text)' }}>
            {t.news.title}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {articles.map((article, i) => {
            const sourceColor = SOURCE_COLORS[article.source] || 'var(--caro-primary)';
            return (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                paddingTop: i > 0 ? '10px' : 0,
                borderTop: i > 0 ? '1px solid var(--caro-border)' : 'none',
              }}>
                <span style={{
                  display: 'inline-block',
                  backgroundColor: sourceColor,
                  color: 'white',
                  fontSize: 'var(--font-small)',
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  marginTop: '2px',
                }}>
                  {article.source}
                </span>
                <div style={{
                  fontSize: 'var(--font-body)',
                  fontWeight: 500,
                  color: 'var(--caro-text)',
                  flex: 1,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.3,
                }}>
                  {article.title}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Default: rotating ticker (compact / wide) ──────────────
  const article = articles[currentIdx];
  if (!article) return null;

  const sourceColor = SOURCE_COLORS[article.source] || 'var(--caro-primary)';

  return (
    <div ref={containerRef} onClick={() => navigate('/news')} style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Source badge */}
        <span style={{
          display: 'inline-block',
          backgroundColor: sourceColor,
          color: 'white',
          fontSize: 'var(--font-small)',
          fontWeight: 700,
          padding: '3px 8px',
          borderRadius: '6px',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
          {article.source}
        </span>
        {/* Headline */}
        <div style={{
          fontSize: 'var(--font-body)',
          fontWeight: 500,
          color: 'var(--caro-text)',
          flex: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {article.title}
        </div>
        {/* Dots indicator */}
        {articles.length > 1 && (
          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
            {articles.map((_, i) => (
              <div
                key={i}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: i === currentIdx ? 'var(--caro-primary)' : 'var(--caro-border)',
                  transition: 'background-color 0.3s',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
